import React from 'react';
import { defineMessages } from 'react-intl-next';
import type { IntlShape } from 'react-intl-next';
import type { EditorState } from 'prosemirror-state';

import { copyToClipboard } from '@atlaskit/editor-common/clipboard';
import LinkIcon from '@atlaskit/icon/core/link';
import type {
	ExtractInjectionAPI,
	Command,
	FloatingToolbarItem,
	FloatingToolbarConfig,
} from '@atlaskit/editor-common/types';
import type { Node } from '@atlaskit/editor-prosemirror/model';
import { NodeSelection } from '@atlaskit/editor-prosemirror/state';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
	INPUT_METHOD,
} from '@atlaskit/editor-common/analytics';
import type { EditorAnalyticsAPI } from '@atlaskit/editor-common/analytics';

import { VIEW_PAGE } from '@confluence/named-routes/entry-points/routes';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';

import { pluginKey } from '../../createPlugin';
import type { ExtensionManagerPlugin } from '../../createPlugin';
import type { ToolbarOverride } from '../toolbarConfigTypes';

// eslint-disable-next-line import/no-cycle
import { CopyLinkToolbarItemTooltipContent } from './components/CopyLinkToolbarItemTooltipContent';

const i18n = defineMessages({
	anchorMacroCopyLinkCTALabel: {
		id: 'fabric-extension-manager-plugin.anchor.copy-link-button.label',
		description: 'Label for the copy link button in the anchor macro floating toolbar',
		defaultMessage: 'Copy link',
	},
});

export const checkIfValidAnchorName = (node: Node) => {
	const anchorName = node.attrs.parameters.macroParams['']?.value;

	if (!anchorName) return false;

	try {
		decodeURIComponent(anchorName);
		return true;
	} catch (_error) {
		return false;
	}
};

export const copyAnchorLinkCommand =
	(node: Node, fireAnalyticsEvent?: EditorAnalyticsAPI['fireAnalyticsEvent']): Command =>
	(state, dispatch) => {
		const { tr } = state;
		const { isLivePage, contentId, spaceKey } = pluginKey.getState(state)?.pageContext ?? {};

		const anchorName = node.attrs.parameters.macroParams['']?.value;
		const pathName = isLivePage
			? window.location.pathname
			: VIEW_PAGE.toUrl({ contentId, spaceKey });

		const setShowAnchorLinkCopiedTooltip = (showAnchorLinkCopiedTooltip: boolean) => {
			dispatch?.(
				tr.setMeta(pluginKey, {
					showAnchorLinkCopiedTooltip,
				}),
			);
		};

		fireAnalyticsEvent?.({
			action: ACTION.CLICKED,
			actionSubject: ACTION_SUBJECT.BUTTON,
			actionSubjectId: ACTION_SUBJECT_ID.COPY_LINK_TO_ANCHOR,
			eventType: EVENT_TYPE.UI,
			attributes: {
				inputMethod: INPUT_METHOD.FLOATING_TB,
				extensionKey: node.attrs?.extensionKey,
				extensionType: node.attrs?.extensionType,
				isLivePage,
			},
		});

		if (dispatch) {
			try {
				decodeURIComponent(anchorName);

				copyToClipboard(`${window.location.origin}${pathName}#${anchorName}`)
					.then(() => {
						setShowAnchorLinkCopiedTooltip(true);
					})
					.catch((error) => {
						getMonitoringClient().submitError(error, {
							attribution: Attribution.PAGE_EXTENSIONS,
						});

						setShowAnchorLinkCopiedTooltip(false);
					});
			} catch (_error) {
				setShowAnchorLinkCopiedTooltip(false);
			}
		}
		return true;
	};

export const overrideToolbarConfigForAnchorMacro = (
	toolbarItems: FloatingToolbarItem<Command>[],
	intl: IntlShape,
	node: Node,
	api?: ExtractInjectionAPI<ExtensionManagerPlugin>,
): FloatingToolbarItem<Command>[] => {
	const updatedToolbarItems = toolbarItems.slice();
	const indexOfFirstSeperator = toolbarItems.map((item) => item.type).indexOf('separator');
	const { fireAnalyticsEvent } = api?.analytics?.actions ?? {};
	const newItemsToInsert: FloatingToolbarItem<Command>[] = [
		{
			id: 'editor.anchor.macro.copy-link',
			type: 'button',
			title: intl.formatMessage(i18n.anchorMacroCopyLinkCTALabel),
			tooltipContent: ({ update }) => (
				<CopyLinkToolbarItemTooltipContent
					node={node}
					intl={intl}
					api={api}
					updateTooltipPosition={update}
				/>
			),
			showTitle: true,
			hideTooltipOnClick: false,
			icon: LinkIcon,
			onClick: copyAnchorLinkCommand(node, fireAnalyticsEvent),
			disabled: !checkIfValidAnchorName(node),
		},
	];
	/**
	 * If there is no separator, 1 group present, add
	 * separator before the new items to create a new visual
	 */
	if (indexOfFirstSeperator === -1) {
		newItemsToInsert.unshift({
			type: 'separator',
			hidden: false,
		});
	} /**
	 * If there is a separator in the toolbar items, and its not the last or first item
	 * add the new items after that separator to create new visual for our newitems
	 *
	 * NOTE: We don't want to add another separator if the separator is the last or first item,
	 * which it should never be because it doesn't make sense, but just in case we will check for that
	 */ else if (indexOfFirstSeperator !== 0 && indexOfFirstSeperator !== toolbarItems.length - 1) {
		newItemsToInsert.push({
			type: 'separator',
			hidden: false,
		});
	}
	updatedToolbarItems.splice(indexOfFirstSeperator + 1, 0, ...newItemsToInsert);
	return updatedToolbarItems;
};

export const anchorMacroToolbarOverride: ToolbarOverride = {
	shouldApplyOverride: (state: EditorState, config: FloatingToolbarConfig) => {
		const isCurrentSelectionAnchorMacro =
			state.selection instanceof NodeSelection &&
			state.selection.node.attrs.extensionType === 'com.atlassian.confluence.macro.core' &&
			state.selection.node.attrs.extensionKey === 'anchor';

		return (
			isCurrentSelectionAnchorMacro &&
			(Array.isArray(config.nodeType)
				? config.nodeType.includes(state.schema.nodes.inlineExtension)
				: config.nodeType === state.schema.nodes.inlineExtension)
		);
	},
	applyOverride: (
		config: FloatingToolbarConfig,
		intl: IntlShape,
		api?: ExtractInjectionAPI<ExtensionManagerPlugin>,
	) => {
		if (Array.isArray(config.items)) {
			const toolbarItems = config.items;
			config.items = (node: Node) => {
				if (
					node.attrs.extensionType === 'com.atlassian.confluence.macro.core' &&
					node.attrs.extensionKey === 'anchor'
				) {
					return overrideToolbarConfigForAnchorMacro(toolbarItems, intl, node, api);
				}
				return toolbarItems;
			};
		}
	},
};
